/**
 * Reading a quantity, whose two sides are different shapes.
 *
 * This is a module rather than two functions inside FieldControl.tsx for one
 * reason worth stating: `GET` answers the COMPOSED string `'3.5 [SI::kg]'` while
 * the write path wants the BARE `3.5`, so both the control that edits a quantity
 * and the form that decides whether one changed have to read the same value the
 * same way. Two copies of that rule would disagree eventually, and the way they
 * would disagree is invisible - a quoted quantity re-parses cleanly, which is
 * how `:>> dryMass = "3.5 [SI::kg]";` reached the model unnoticed in the first
 * place.
 *
 * Nothing here knows a field NAME. A quantity's unit is a property of the field
 * (`FieldSchema.unit`), which is what lets a fourth quantity added to the profile
 * work with no change on this side.
 */

/** `3.5 [SI::kg]`, as the projection answers a quantity. */
const COMPOSED = /^\s*([^[\]]*?)\s*\[\s*([^[\]]+?)\s*\]\s*$/

/**
 * The number and the unit out of whatever a quantity field carries.
 *
 * A value with no brackets comes back verbatim - it is either a bare number an
 * earlier edit put there or text half-way to being one, and rewriting either
 * would fight the person typing. `null`/`undefined` and whitespace read as
 * unstated; a numeric `0` does NOT, which is the distinction the whole kind
 * exists to keep.
 *
 * @param value whatever arrived on the row or from a previous edit
 */
export function splitQuantity(value: unknown): { number: string; unit: string } {
  if (value === null || value === undefined) return { number: '', unit: '' }
  const text = String(value)
  const composed = COMPOSED.exec(text)
  if (composed) return { number: composed[1], unit: composed[2] }
  return { number: text.trim() === '' ? '' : text, unit: '' }
}

/**
 * The number a quantity states, or null when it states NOTHING.
 *
 * Unset is not zero, and this is where that stays honest: a part genuinely
 * drawing 0 W is a different statement from nobody having filled the figure in,
 * and the rollup reports the second as partial and names the part rather than
 * summing a confident wrong number.
 *
 * Text nobody can parse answers `NaN`, which compares unequal to everything
 * including itself - so it always reads as an edit worth sending, and worth
 * being refused for with the server's own message.
 */
export function quantityStated(value: unknown): number | null {
  const { number } = splitQuantity(value)
  return number.trim() === '' ? null : Number(number)
}
